import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Github,
  Send,
  ArrowUp,
  CheckCircle2
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const footerLinks = {
    Company: [
      { label: 'About Us', path: '/about' },
      { label: 'Vision & Mission', path: '/vision' },
      { label: 'Contact', path: '/contact' }
    ],
    Services: [
      { label: 'Data Center Migration', path: '/services/broadcom-migration' },
      { label: 'Office Recabling', path: '/services/office-recabling' },
      { label: 'Switch & Firewall', path: '/services/switch-firewall' },
      { label: 'AV Solutions', path: '/services/audio-video-solutions' }
    ],
    Resources: [
      { label: 'FAQ', path: '/faq' },
    ]
  };

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com', label: 'Facebook', color: 'hover:text-blue-600' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter', color: 'hover:text-sky-500' },
    { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn', color: 'hover:text-blue-700' },
    { icon: Instagram, href: 'https://instagram.com', label: 'Instagram', color: 'hover:text-pink-600' },
    { icon: Github, href: 'https://github.com', label: 'GitHub', color: 'hover:text-gray-200' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setError('');

    const value = email.trim().toLowerCase();
    if (!value) return;

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!emailOk) {
      setError('Enter a valid email');
      return;
    }

    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
      setError('Supabase configuration missing');
      return;
    }

    setLoading(true);
    try {
      const { error: insertError } = await supabase
        .from('newsletter_subscribers')
        .insert([
          {
            email: value,
            source: 'footer',
            user_agent: navigator.userAgent,
            path: window.location?.pathname || '/'
          }
        ]);

      if (insertError) {
        if (insertError.code === '23505' || /duplicate/i.test(insertError.message)) {
          setSubscribed(true);
          setEmail('');
          setTimeout(() => setSubscribed(false), 5000);
          return;
        }
        setError('Subscription failed. Please try again.');
        return;
      }

      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 5000);
    } catch {
      setError('Unexpected error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-slate-900 to-blue-900 text-gray-300 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12"
        >
          {/* Brand Section */}
          <motion.div variants={itemVariants} className="lg:col-span-2 space-y-6">
            <Link to="/" className="flex items-center space-x-3 group">
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 p-3 rounded-xl shadow-lg"
              >
                <Briefcase className="h-7 w-7 text-white" />
              </motion.div>
              <span className="text-2xl font-bold text-white">Tasks IT Services</span>
            </Link>

            <p className="text-gray-400 leading-relaxed max-w-sm">
              Your trusted partner for enterprise IT solutions. We deliver secure, scalable infrastructure
              and cutting-edge technology services.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="mailto:GlennRosas@hotmail.com"
                className="flex items-center gap-3 text-gray-400 hover:text-cyan-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-cyan-500/10 transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <span className="text-sm">GlennRosas@hotmail.com</span>
              </a>
              <a
                href="tel:+81702633830"
                className="flex items-center gap-3 text-gray-400 hover:text-cyan-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-cyan-500/10 transition-colors">
                  <Phone className="h-5 w-5" />
                </div>
                <span className="text-sm">+81 70-2633-2830</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="text-sm">
                  <div>Machida City</div>
                  <div>Tokyo, Japan</div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            {/* <div className="flex items-center gap-3 pt-2">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className={`
                    w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center
                    text-gray-400 ${social.color} transition-all
                    hover:bg-white/10 hover:shadow-lg
                  `}
                >
                  <social.icon className="h-5 w-5" />
                </motion.a>
              ))}
            </div> */}
          </motion.div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <motion.div key={title} variants={itemVariants} className="space-y-4">
              <h3 className="text-white font-semibold text-lg border-b border-white/10 pb-2">
                {title}
              </h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-cyan-400 transition-colors flex items-center gap-2 group text-sm"
                    >
                      <span className="w-1 h-1 rounded-full bg-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="text-gray-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} Tasks IT Services. All rights reserved.
          </p>
        </motion.div>
      </div>

      {/* Scroll to Top Button */}
      <motion.button
        onClick={scrollToTop}
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.1, y: -2 }}
        whileTap={{ scale: 0.9 }}
        className="
          fixed bottom-8 right-8 z-40
          w-12 h-12 rounded-full
          bg-gradient-to-r from-blue-600 to-cyan-500
          text-white shadow-lg hover:shadow-xl
          flex items-center justify-center
          transition-all duration-300
        "
        aria-label="Scroll to top"
      >
        <ArrowUp className="h-6 w-6" />
      </motion.button>
    </footer>
  );
};

export default Footer;